import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useIntl } from 'react-intl'

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: '400px',
    maxWidth: '600px',
  },
  observationText: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: 1.6,
  },
  noObservation: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}))

/**
 * @typedef {object} ObservationsDialogProps
 * @property {boolean} open - Si es true, el diálogo está visible.
 * @property {function(): void} onClose - Callback ejecutado cuando se cierra el diálogo.
 * @property {string} [observaciones] - El texto de las observaciones a mostrar. Si es falsy, muestra "Sin observaciones".
 */

/**
 * Un componente de diálogo modal para mostrar observaciones o comentarios largos.
 *
 * Proporciona un diálogo centrado con título, contenido de observaciones formateado
 * (respetando saltos de línea y wrapping de palabras), y botón de cierre. Ideal para
 * mostrar el contenido completo de campos de texto truncados en tablas o listas.
 *
 * @component
 * @example
 * const MyTable = () => {
 *   const [dialogOpen, setDialogOpen] = useState(false);
 *   const [selectedObservation, setSelectedObservation] = useState('');
 *
 *   const handleShowObservation = (observation) => {
 *     setSelectedObservation(observation);
 *     setDialogOpen(true);
 *   };
 *
 *   const handleClose = () => {
 *     setDialogOpen(false);
 *   };
 *
 *   return (
 *     <>
 *       <TableCell>
 *         <TruncatedObservation
 *           text="Esta es una observación muy larga..."
 *           onClick={() => handleShowObservation("Esta es una observación muy larga...")}
 *         />
 *       </TableCell>
 *
 *       <ObservationsDialog
 *         open={dialogOpen}
 *         onClose={handleClose}
 *         observaciones={selectedObservation}
 *       />
 *     </>
 *   );
 * };
 *
 * @param {ObservationsDialogProps} props - Las props del componente ObservationsDialog.
 */
export const ObservationsDialog = ({ open, onClose, observaciones }) => {
  const classes = useStyles()
  const intl = useIntl()

  const title =
    intl.formatMessage({
      id: 'pages.application.detail.history.tableFields.observaciones',
    }) || 'Observaciones'

  const closeButtonText = intl.formatMessage({
    id: 'global.close',
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography className={observaciones ? classes.observationText : classes.noObservation}>
          {observaciones || 'Sin observaciones'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {closeButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
